import type { ManageFirmProfessionalProfilesResponse } from "../../interfaces/models/IManageFirmOverview";
import { MANAGE_FIRM_STATS_SECTION_TITLE_CLASS } from "./manageFirmStatsPanelStyles";

const FOOTNOTE = "*M: Members | A: Accepted Members";

function formatCount(value: number | null | undefined): string {
  if (value == null) {
    return "-";
  }
  return value.toLocaleString("en-US");
}

type MetricRow = {
  label: string;
  value: number;
};

function MetricCard({ label, value }: MetricRow): JSX.Element {
  return (
    <div className="flex min-h-[4.5rem] items-center justify-between gap-4 rounded-md bg-accent-Default-light px-4 py-3">
      <span className="min-w-0 flex-1 text-sm leading-snug text-gray-font">
        {label}
      </span>
      <span className="shrink-0 text-xl font-semibold tabular-nums text-gray-font">
        {formatCount(value)}
      </span>
    </div>
  );
}

function MetricGrid({ rows }: { rows: MetricRow[] }): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {rows.map((row) => (
        <MetricCard key={row.label} label={row.label} value={row.value} />
      ))}
    </div>
  );
}

type ManageFirmProfessionalProfilesPanelProps = {
  data: ManageFirmProfessionalProfilesResponse | null | undefined;
  loading?: boolean;
};

export default function ManageFirmProfessionalProfilesPanel({
  data,
  loading = false,
}: ManageFirmProfessionalProfilesPanelProps): JSX.Element {
  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-56 rounded bg-gray-200" />
          <div className="h-40 rounded bg-gray-100" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-gray-font">
          Professional Profiles is unavailable.
        </p>
      </div>
    );
  }

  const databaseRows: MetricRow[] = [
    {
      label: "Total Offices of Member Firms (M)",
      value: data.databaseInformation.totalOfficesOfMemberFirms,
    },
    {
      label: "Total Individual Profiles in database (M)",
      value: data.databaseInformation.totalIndividualProfiles,
    },
    {
      label: "Total Individual Profiles in database - WSG Jurisdictions (M)",
      value: data.databaseInformation.totalIndividualProfilesWsgJurisdictions,
    },
    {
      label: "Total Hidden profiles in database - (M+A)",
      value: data.databaseInformation.totalHiddenProfiles,
    },
  ];

  const reportedRows: MetricRow[] = [
    {
      label: "Total Professionals worldwide based on firms' data (M+A)",
      value: data.reportedByFirm.totalProfessionalsWorldwide,
    },
    {
      label:
        "Total Professionals in WSG jurisdictions based on firms' data - Used for billing (M+A)",
      value: data.reportedByFirm.totalProfessionalsInWsgJurisdictions,
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full flex-col rounded-md border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className={`${MANAGE_FIRM_STATS_SECTION_TITLE_CLASS} mb-5`}>
        Professional Profiles
      </h3>

      <div className="space-y-6">
        <section>
          <h4 className="mb-3 text-sm font-semibold text-gray-font">
            WSG Database Information
          </h4>
          <MetricGrid rows={databaseRows} />
          <p className="mt-3 text-xs italic text-gray-500">{FOOTNOTE}</p>
        </section>

        <section className="border-t border-gray-100 pt-6">
          <h4 className="mb-3 text-sm font-semibold text-gray-font">
            Number Reported by Firm
          </h4>
          <MetricGrid rows={reportedRows} />
          <p className="mt-3 text-xs italic text-gray-500">{FOOTNOTE}</p>
        </section>
      </div>
    </div>
  );
}
